import { useState, type Dispatch, type ReactNode, type SetStateAction } from 'react';
import { Icon } from '~/components/ui';
import { useT } from '~/i18n';
import VoiceAnswer from './VoiceAnswer';
import WhatsappPhoneInput from '~/components/WhatsappPhoneInput';
import {
  DUTIES_FIELD,
  EDUCATION_FIELD,
  MAX_DUTIES,
  MAX_EDUCATION,
  MAX_LANGUAGES,
  MAX_LAST_JOBS,
  MAX_SKILLS,
  PHYSICAL_CHIPS,
  PHYSICAL_CHIP_IDS,
  PHYSICAL_FIELD,
  SCREENING_LANGUAGE_LEVELS,
  SCREENING_STEPS,
  SCREENING_VISA_TYPES,
  SCREENING_WORK_AREAS,
  SKILLS_FIELD,
  START_WHEN_OPTIONS,
  dropDutiesIndex,
  emptyJob,
  noteInputMode,
  noteQuality,
  physicalRoomLeft,
  splitSkills,
  startWhenOptionFor,
  validateStep,
  type FieldErrors,
  type ScreeningDraft,
  type ScreeningVisaType,
  type ScreeningWorkArea,
} from '../lib/screening';
import type { AnswerQuality, InputMode } from '../lib/voice';

/**
 * El cuestionario, un grupo temático por pantalla.
 *
 * ── POR QUÉ MULTI-PASO Y NO UN FORMULARIO LARGO ────────────────────────────
 * La mayoría de quien llena esto entra por teléfono. Doce campos en una sola
 * página son doce campos que hay que desplazar con el teclado abierto tapando
 * la mitad de la pantalla, y el primer error aparece recién al final, cuando ya
 * no se sabe cuál de los doce fue. Un grupo por pantalla valida al avanzar: el
 * error sale al lado del campo que lo causó, mientras todavía está a la vista.
 *
 * El componente es controlado —el borrador y el paso viven en la pantalla— para
 * que el guardado en sessionStorage sea un solo efecto en un solo lugar y no
 * una copia por campo.
 */

/** Campos del borrador cuyo error viaja con la ruta anidada del contrato. */
const NESTED_ERROR_KEY: Record<string, string> = {
  relocate: 'availability.relocate',
  start_when: 'availability.start_when',
};

interface Props {
  draft: ScreeningDraft;
  /**
   * Acepta la forma funcional a propósito. El coach contesta ASÍNCRONO —medio
   * segundo después de que la persona salió del campo— y para entonces el
   * `draft` que su render capturó puede estar viejo. Anotar la calidad sobre un
   * borrador viejo borra lo que se escribió mientras tanto.
   */
  onDraftChange: Dispatch<SetStateAction<ScreeningDraft>>;
  step: number;
  onStepChange: (step: number) => void;
  errors: FieldErrors;
  onErrorsChange: (errors: FieldErrors) => void;
  submitting: boolean;
  submitError: string | null;
  onSubmit: () => void;
  /** Volver a la doble puerta del alta. */
  onExit: () => void;
}

export default function ScreeningWizard({
  draft,
  onDraftChange,
  step,
  onStepChange,
  errors,
  onErrorsChange,
  submitting,
  submitError,
  onSubmit,
  onExit,
}: Props) {
  const t = useT('screening');
  const current = SCREENING_STEPS[Math.min(step, SCREENING_STEPS.length - 1)];
  const isLast = step === SCREENING_STEPS.length - 1;
  const progress = Math.round(((step + 1) / SCREENING_STEPS.length) * 100);

  /** El error se apaga en cuanto la persona toca el campo que lo causó. */
  const clearError = (key: string) => {
    if (!errors[key]) return;
    const next = { ...errors };
    delete next[key];
    onErrorsChange(next);
  };

  const set = <K extends keyof ScreeningDraft>(key: K, value: ScreeningDraft[K]) => {
    onDraftChange({ ...draft, [key]: value });
    // Dos campos viven anidados en el cuerpo (`availability.*`) y su error se
    // guarda con la ruta del servidor, no con el nombre del borrador.
    clearError(NESTED_ERROR_KEY[key as string] ?? (key as string));
  };

  /**
   * El veredicto del coach, anotado sobre el borrador VIVO.
   *
   * Nunca frena nada: `answer_quality` es analítica de colocación, no una
   * validación. Una respuesta `thin` sale igual, con la misma hoja de vida y sin
   * un aviso más en pantalla.
   */
  const noteAnswer = (field: string, quality: AnswerQuality) =>
    onDraftChange((prev) => ({
      ...prev,
      answer_quality: noteQuality(prev.answer_quality, field, quality),
    }));

  const goNext = () => {
    const stepErrors = validateStep(current.id, draft);
    if (Object.keys(stepErrors).length) {
      onErrorsChange(stepErrors);
      // El primer campo con error se lleva el foco: en un teléfono el error
      // puede quedar fuera de la pantalla y nadie lo va a buscar.
      focusFirstError(stepErrors);
      return;
    }
    onErrorsChange({});
    if (isLast) onSubmit();
    else onStepChange(step + 1);
  };

  const goBack = () => {
    onErrorsChange({});
    if (step === 0) onExit();
    else onStepChange(step - 1);
  };

  return (
    <div className="mx-auto w-full max-w-xl py-6 space-y-5">
      <header className="space-y-3">
        <div className="flex items-baseline justify-between gap-3">
          <span className="brand-label">{t.wizard.stepOf(step + 1, SCREENING_STEPS.length)}</span>
          <span className="text-xs hush">{progress}%</span>
        </div>

        <div
          className="h-1.5 rounded-full overflow-hidden"
          style={{ background: 'var(--surface-sunk)' }}
          role="progressbar"
          aria-label={t.wizard.progressLabel}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={progress}
        >
          <div
            className="h-full rounded-full bg-brand-gradient"
            style={{ width: `${progress}%`, transition: 'width var(--t-move) var(--ease-enter)' }}
          />
        </div>

        <div>
          <h1 className="text-2xl font-semibold">{t.steps[current.id].title}</h1>
          <p className="hush text-sm mt-1">{t.steps[current.id].lede}</p>
        </div>
      </header>

      <div className="card rise">
        <div className="card-body space-y-5">
          {current.id === 'basics' && <BasicsStep draft={draft} errors={errors} set={set} />}
          {current.id === 'target' && <TargetStep draft={draft} errors={errors} set={set} />}
          {current.id === 'experience' && (
            <ExperienceStep
              draft={draft}
              errors={errors}
              onDraftChange={onDraftChange}
              onQuality={noteAnswer}
              onClearError={clearError}
            />
          )}
          {current.id === 'skills' && (
            <SkillsStep
              draft={draft}
              errors={errors}
              set={set}
              onDraftChange={onDraftChange}
              onQuality={noteAnswer}
              onClearError={clearError}
            />
          )}
          {current.id === 'availability' && (
            <AvailabilityStep
              draft={draft}
              errors={errors}
              set={set}
              onDraftChange={onDraftChange}
              onQuality={noteAnswer}
              onClearError={clearError}
            />
          )}
        </div>
      </div>

      {submitError && <div className="alert alert-error text-sm">{submitError}</div>}

      <div className="flex gap-3">
        <button type="button" className="btn btn-ghost" onClick={goBack} disabled={submitting}>
          {step === 0 ? t.wizard.cancel : t.wizard.back}
        </button>
        <button type="button" className="btn btn-primary flex-1" onClick={goNext} disabled={submitting}>
          {submitting && <span className="loading loading-spinner loading-sm" />}
          {isLast ? t.wizard.create : t.wizard.next}
        </button>
      </div>

      {isLast && !submitting && (
        <p className="text-xs hush text-center">{t.wizard.createNote}</p>
      )}
    </div>
  );
}

function focusFirstError(errors: FieldErrors): void {
  // Las rutas llevan puntos ('last_jobs.0.end_year'), que dentro de comillas en
  // un selector de atributo son un carácter más y no necesitan escaparse.
  const first = Object.keys(errors).find((path) => !path.includes('"'));
  if (!first) return;
  const el = document.querySelector<HTMLElement>(`[data-field="${first}"]`);
  el?.focus();
}

/* ── piezas compartidas ──────────────────────────────────────────────────── */

function Field({
  label,
  hint,
  error,
  optional,
  htmlFor,
  children,
}: {
  label: string;
  hint?: string;
  error?: string;
  optional?: boolean;
  htmlFor?: string;
  children: ReactNode;
}) {
  const t = useT('screening');
  return (
    <div className="form-control">
      <label className="label pb-1" htmlFor={htmlFor}>
        <span className="label-text font-medium">
          {label}
          {optional && <span className="hush font-normal">{t.wizard.optional}</span>}
        </span>
      </label>
      {children}
      {hint && !error && <p className="text-xs hush mt-1">{hint}</p>}
      {/* Coral: es fricción, algo que frena a la persona. */}
      {error && (
        <p className="text-xs text-friction mt-1" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}

/** Una píldora que se prende. La geometría de marca ya la pone `.chip`. */
function Toggle({
  selected,
  onClick,
  children,
  field,
}: {
  selected: boolean;
  onClick: () => void;
  children: ReactNode;
  field?: string;
}) {
  return (
    <button
      type="button"
      className="chip"
      aria-pressed={selected}
      data-field={field}
      onClick={onClick}
    >
      {selected && <Icon name="check" size={14} />}
      {children}
    </button>
  );
}

/* ── paso 1: datos básicos ───────────────────────────────────────────────── */

type Setter = <K extends keyof ScreeningDraft>(key: K, value: ScreeningDraft[K]) => void;

function BasicsStep({
  draft,
  errors,
  set,
}: {
  draft: ScreeningDraft;
  errors: FieldErrors;
  set: Setter;
}) {
  const t = useT('screening');
  return (
    <>
      <Field label={t.basics.fullName} error={errors.full_name} htmlFor="full_name">
        <input
          id="full_name"
          data-field="full_name"
          className={`input w-full${errors.full_name ? ' input-error' : ''}`}
          value={draft.full_name}
          autoComplete="name"
          placeholder={t.basics.fullNamePlaceholder}
          onChange={(e) => set('full_name', e.target.value)}
        />
      </Field>

      <Field
        label={t.basics.cityCountry}
        error={errors.city_country}
        htmlFor="city_country"
        hint={t.basics.cityCountryHint}
      >
        <input
          id="city_country"
          data-field="city_country"
          className={`input w-full${errors.city_country ? ' input-error' : ''}`}
          value={draft.city_country}
          autoComplete="address-level2"
          onChange={(e) => set('city_country', e.target.value)}
        />
      </Field>

      <Field
        label={t.basics.phone}
        optional
        error={errors.phone}
        htmlFor="phone"
        hint={t.basics.phoneHint}
      >
        {/* El WhatsApp, con país y número (2026-10-08): el mismo campo que el
            alta con archivo y que Perfil, para no preguntarlo dos veces. Al
            crear la hoja de vida se guarda también en la membresía. */}
        <WhatsappPhoneInput
          id="phone"
          dataField="phone"
          value={draft.phone}
          invalid={!!errors.phone}
          onChange={(value) => set('phone', value)}
        />
      </Field>
    </>
  );
}

/* ── paso 2: visa y áreas ────────────────────────────────────────────────── */

function TargetStep({
  draft,
  errors,
  set,
}: {
  draft: ScreeningDraft;
  errors: FieldErrors;
  set: Setter;
}) {
  const t = useT('screening');
  const toggleVisa = (value: ScreeningVisaType) =>
    set(
      'visa_types',
      draft.visa_types.includes(value)
        ? draft.visa_types.filter((v) => v !== value)
        : [...draft.visa_types, value],
    );

  const toggleArea = (value: ScreeningWorkArea) =>
    set(
      'work_areas',
      draft.work_areas.includes(value)
        ? draft.work_areas.filter((a) => a !== value)
        : [...draft.work_areas, value],
    );

  return (
    <>
      <Field
        label={t.target.visa}
        error={errors.visa_types}
        hint={t.target.visaHint}
      >
        <div className="space-y-2 mt-1">
          {SCREENING_VISA_TYPES.map((visa, i) => {
            const selected = draft.visa_types.includes(visa);
            return (
              <button
                key={visa}
                type="button"
                data-field={i === 0 ? 'visa_types' : undefined}
                aria-pressed={selected}
                onClick={() => toggleVisa(visa)}
                className="w-full text-left p-3 flex items-start gap-3"
                style={{
                  borderRadius: 'var(--r-inner)',
                  border: `1px solid ${selected ? 'var(--hairline-hi)' : 'var(--hairline)'}`,
                  background: selected ? 'var(--surface-hi)' : 'transparent',
                }}
              >
                <span
                  className="grid place-items-center shrink-0 mt-0.5"
                  style={{
                    width: 20,
                    height: 20,
                    borderRadius: 999,
                    border: `1px solid ${selected ? 'transparent' : 'var(--hairline)'}`,
                    background: selected ? 'var(--purple-cta)' : 'transparent',
                    color: '#fff',
                  }}
                >
                  {selected && <Icon name="check" size={13} />}
                </span>
                <span className="min-w-0">
                  <span className="block font-medium text-sm">{t.options.visa[visa].label}</span>
                  <span className="block text-xs hush">{t.options.visa[visa].hint}</span>
                </span>
              </button>
            );
          })}
        </div>
      </Field>

      <Field
        label={t.target.areas}
        error={errors.work_areas}
        hint={t.target.areasHint}
      >
        <div className="flex flex-wrap gap-2 mt-1">
          {SCREENING_WORK_AREAS.map((area, i) => (
            <Toggle
              key={area}
              field={i === 0 ? 'work_areas' : undefined}
              selected={draft.work_areas.includes(area)}
              onClick={() => toggleArea(area)}
            >
              {t.options.workArea[area]}
            </Toggle>
          ))}
        </div>
      </Field>

      <Field
        label={t.target.years}
        error={errors.years_experience}
        htmlFor="years_experience"
        hint={t.target.yearsHint}
      >
        <input
          id="years_experience"
          data-field="years_experience"
          type="number"
          inputMode="numeric"
          min={0}
          max={60}
          step={1}
          className={`input w-full no-spinner${errors.years_experience ? ' input-error' : ''}`}
          value={draft.years_experience}
          onChange={(e) => set('years_experience', e.target.value)}
        />
      </Field>
    </>
  );
}

/* ── paso 3: experiencia ─────────────────────────────────────────────────── */

function ExperienceStep({
  draft,
  errors,
  onDraftChange,
  onQuality,
  onClearError,
}: {
  draft: ScreeningDraft;
  errors: FieldErrors;
  onDraftChange: Dispatch<SetStateAction<ScreeningDraft>>;
  onQuality: (field: string, quality: AnswerQuality) => void;
  onClearError: (key: string) => void;
}) {
  const t = useT('screening');
  const updateJob = (index: number, patch: Partial<ScreeningDraft['last_jobs'][number]>) => {
    onDraftChange((prev) => ({
      ...prev,
      last_jobs: prev.last_jobs.map((job, i) => (i === index ? { ...job, ...patch } : job)),
    }));
  };

  /** El texto de "qué hacías" y CÓMO llegó son un solo cambio del borrador. */
  const updateDuties = (index: number, value: string, mode: InputMode) => {
    onDraftChange((prev) => ({
      ...prev,
      last_jobs: prev.last_jobs.map((job, i) => (i === index ? { ...job, duties: value } : job)),
      input_mode: noteInputMode(prev.input_mode, DUTIES_FIELD(index), mode, value),
    }));
    onClearError(`last_jobs.${index}.duties`);
  };

  const addJob = () =>
    onDraftChange((prev) => ({ ...prev, last_jobs: [...prev.last_jobs, emptyJob()] }));
  // Borrar un empleo corre a los que quedan detrás, y sus llaves de analítica
  // van pegadas al índice: sin esto, la calidad y el modo de un relato se
  // quedarían anotados sobre el empleo de otro.
  const removeJob = (index: number) =>
    onDraftChange((prev) => ({
      ...prev,
      last_jobs: prev.last_jobs.filter((_, i) => i !== index),
      answer_quality: dropDutiesIndex(prev.answer_quality, index),
      input_mode: dropDutiesIndex(prev.input_mode, index),
    }));

  return (
    <>
      {draft.last_jobs.length === 0 && (
        <p className="hush text-sm">{t.experience.intro}</p>
      )}

      {draft.last_jobs.map((job, i) => (
        <div
          key={i}
          className="space-y-4 p-4"
          style={{
            borderRadius: 'var(--r-inner)',
            border: '1px solid var(--hairline)',
            background: 'var(--surface-hi)',
          }}
        >
          <div className="flex items-center justify-between gap-2">
            <span className="brand-label">{t.experience.job(i + 1)}</span>
            <button type="button" className="btn btn-ghost btn-sm" onClick={() => removeJob(i)}>
              {t.experience.remove}
            </button>
          </div>

          <Field label={t.experience.company} error={errors[`last_jobs.${i}.company`]}>
            <input
              data-field={`last_jobs.${i}.company`}
              className={`input w-full${errors[`last_jobs.${i}.company`] ? ' input-error' : ''}`}
              value={job.company}
              onChange={(e) => updateJob(i, { company: e.target.value })}
            />
          </Field>

          <Field label={t.experience.role} error={errors[`last_jobs.${i}.role`]}>
            <input
              data-field={`last_jobs.${i}.role`}
              className={`input w-full${errors[`last_jobs.${i}.role`] ? ' input-error' : ''}`}
              value={job.role}
              placeholder={t.experience.rolePlaceholder}
              onChange={(e) => updateJob(i, { role: e.target.value })}
            />
          </Field>

          <div className="grid grid-cols-2 gap-3">
            <Field label={t.experience.startYear} error={errors[`last_jobs.${i}.start_year`]}>
              <input
                data-field={`last_jobs.${i}.start_year`}
                type="number"
                inputMode="numeric"
                placeholder="2021"
                className={`input w-full no-spinner${errors[`last_jobs.${i}.start_year`] ? ' input-error' : ''}`}
                value={job.start_year}
                onChange={(e) => updateJob(i, { start_year: e.target.value })}
              />
            </Field>

            <Field label={t.experience.endYear} error={errors[`last_jobs.${i}.end_year`]}>
              <input
                data-field={`last_jobs.${i}.end_year`}
                type="number"
                inputMode="numeric"
                placeholder="2024"
                disabled={job.current}
                className={`input w-full no-spinner${errors[`last_jobs.${i}.end_year`] ? ' input-error' : ''}`}
                value={job.current ? '' : job.end_year}
                onChange={(e) => updateJob(i, { end_year: e.target.value })}
              />
            </Field>
          </div>

          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              className="checkbox checkbox-sm"
              checked={job.current}
              onChange={(e) => updateJob(i, { current: e.target.checked, end_year: '' })}
            />
            <span className="text-sm">{t.experience.current}</span>
          </label>

          <Field
            label={t.experience.duties}
            error={errors[`last_jobs.${i}.duties`]}
            htmlFor={`duties-${i}`}
          >
            <VoiceAnswer
              id={`duties-${i}`}
              field={DUTIES_FIELD(i)}
              dataField={`last_jobs.${i}.duties`}
              // `question` no se ve: VIAJA al coach como parte del cuerpo, en el
              // idioma de la interfaz (el coach repregunta en ese mismo idioma).
              question={t.coachQuestions.duties}
              invitation={t.experience.dutiesInvitation}
              placeholder={t.experience.dutiesPlaceholder}
              value={job.duties}
              maxLength={MAX_DUTIES}
              rows={4}
              invalid={!!errors[`last_jobs.${i}.duties`]}
              onChange={(value, mode) => updateDuties(i, value, mode)}
              onQuality={(quality) => onQuality(DUTIES_FIELD(i), quality)}
            />
          </Field>
        </div>
      ))}

      {draft.last_jobs.length < MAX_LAST_JOBS && (
        <button type="button" className="btn btn-outline w-full gap-2" onClick={addJob}>
          <Icon name="plus" size={16} />
          {draft.last_jobs.length === 0 ? t.experience.addFirst : t.experience.addAnother}
        </button>
      )}

      {draft.last_jobs.length === 0 && (
        <p className="text-xs hush">{t.experience.noJobs}</p>
      )}
    </>
  );
}

/* ── paso 4: habilidades, idiomas y estudios ─────────────────────────────── */

function SkillsStep({
  draft,
  errors,
  set,
  onDraftChange,
  onQuality,
  onClearError,
}: {
  draft: ScreeningDraft;
  errors: FieldErrors;
  set: Setter;
  onDraftChange: Dispatch<SetStateAction<ScreeningDraft>>;
  onQuality: (field: string, quality: AnswerQuality) => void;
  onClearError: (key: string) => void;
}) {
  // El dictado se queda EN ESPERA hasta que la persona confirma: el contrato
  // pide una lista, y una transcripción cruda no es una lista. El texto vive
  // fuera del borrador porque no es una respuesta, es materia prima.
  const t = useT('screening');
  const [pending, setPending] = useState('');
  const [pendingMode, setPendingMode] = useState<InputMode>('text');

  const commitSkills = () => {
    const pieces = splitSkills(pending);
    setPending('');
    if (!pieces.length) return;

    const merged = [...draft.skills_equipment];
    for (const piece of pieces) {
      if (merged.length >= MAX_SKILLS) break;
      if (merged.some((skill) => skill.toLowerCase() === piece.toLowerCase())) continue;
      merged.push(piece);
    }
    onDraftChange((prev) => ({
      ...prev,
      skills_equipment: merged,
      input_mode: noteInputMode(prev.input_mode, SKILLS_FIELD, pendingMode, merged.join(' ')),
    }));
    setPendingMode('text');
  };

  const removeSkill = (value: string) =>
    set(
      'skills_equipment',
      draft.skills_equipment.filter((s) => s !== value),
    );

  const updateLanguage = (index: number, patch: Partial<ScreeningDraft['languages'][number]>) =>
    onDraftChange((prev) => ({
      ...prev,
      languages: prev.languages.map((row, i) => (i === index ? { ...row, ...patch } : row)),
    }));

  return (
    <>
      <Field
        label={t.skills.skills}
        optional
        error={errors.skills_equipment}
        htmlFor="skill_input"
      >
        {/* Sin coach: una enumeración de máquinas no tiene nada que repreguntar,
            y una repregunta sobre una lista sólo la haría más larga, no mejor. */}
        <VoiceAnswer
          id="skill_input"
          field={SKILLS_FIELD}
          question={t.coachQuestions.skills}
          invitation={t.skills.skillsInvitation}
          placeholder={t.skills.skillsPlaceholder}
          value={pending}
          maxLength={400}
          rows={2}
          coach={false}
          onChange={(value, mode) => {
            setPending(value);
            if (mode === 'voice') setPendingMode('voice');
          }}
          footer={
            pending.trim() ? (
              <button type="button" className="btn btn-outline btn-sm gap-2" onClick={commitSkills}>
                <Icon name="plus" size={14} />
                {t.skills.addToList}
              </button>
            ) : null
          }
        />

        {draft.skills_equipment.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-3">
            {draft.skills_equipment.map((skill) => (
              <button
                key={skill}
                type="button"
                className="chip"
                onClick={() => removeSkill(skill)}
                aria-label={t.skills.removeSkill(skill)}
              >
                {skill}
                <span aria-hidden="true" style={{ color: 'var(--ink-faint)' }}>
                  ✕
                </span>
              </button>
            ))}
          </div>
        )}
      </Field>

      <Field label={t.skills.languages} optional error={errors.languages}>
        <div className="space-y-2">
          {draft.languages.map((row, i) => (
            <div key={i} className="flex gap-2">
              <input
                data-field={`languages.${i}.language`}
                className={`input flex-1${errors[`languages.${i}.language`] ? ' input-error' : ''}`}
                placeholder={t.skills.languagePlaceholder}
                value={row.language}
                onChange={(e) => updateLanguage(i, { language: e.target.value })}
              />
              <select
                className="select"
                aria-label={t.skills.levelOf(row.language)}
                value={row.level}
                onChange={(e) =>
                  updateLanguage(i, { level: e.target.value as ScreeningDraft['languages'][number]['level'] })
                }
              >
                {SCREENING_LANGUAGE_LEVELS.map((level) => (
                  <option key={level} value={level}>
                    {t.options.languageLevel[level]}
                  </option>
                ))}
              </select>
              <button
                type="button"
                className="btn btn-ghost"
                aria-label={t.skills.removeLanguage(i + 1)}
                onClick={() =>
                  onDraftChange({
                    ...draft,
                    languages: draft.languages.filter((_, index) => index !== i),
                  })
                }
              >
                ✕
              </button>
            </div>
          ))}

          {draft.languages.map((row, i) =>
            errors[`languages.${i}.language`] ? (
              <p key={`err-${i}`} className="text-xs text-friction" role="alert">
                {errors[`languages.${i}.language`]}
              </p>
            ) : null,
          )}

          {draft.languages.length < MAX_LANGUAGES && (
            <button
              type="button"
              className="btn btn-outline btn-sm gap-2"
              onClick={() =>
                onDraftChange({
                  ...draft,
                  languages: [...draft.languages, { language: '', level: 'basic' }],
                })
              }
            >
              <Icon name="plus" size={14} />
              {t.skills.addLanguage}
            </button>
          )}
        </div>
      </Field>

      <Field label={t.skills.education} error={errors.education} htmlFor="education">
        <VoiceAnswer
          id="education"
          field={EDUCATION_FIELD}
          question={t.coachQuestions.education}
          invitation={t.skills.educationInvitation}
          placeholder={t.skills.educationPlaceholder}
          value={draft.education}
          maxLength={MAX_EDUCATION}
          rows={3}
          invalid={!!errors.education}
          onChange={(value, mode) => {
            onDraftChange((prev) => ({
              ...prev,
              education: value,
              input_mode: noteInputMode(prev.input_mode, EDUCATION_FIELD, mode, value),
            }));
            onClearError(EDUCATION_FIELD);
          }}
          onQuality={(quality) => onQuality(EDUCATION_FIELD, quality)}
        />
      </Field>
    </>
  );
}

/* ── paso 5: disponibilidad ──────────────────────────────────────────────── */

function AvailabilityStep({
  draft,
  errors,
  set,
  onDraftChange,
  onQuality,
  onClearError,
}: {
  draft: ScreeningDraft;
  errors: FieldErrors;
  set: Setter;
  onDraftChange: Dispatch<SetStateAction<ScreeningDraft>>;
  onQuality: (field: string, quality: AnswerQuality) => void;
  onClearError: (key: string) => void;
}) {
  const t = useT('screening');
  const preset = startWhenOptionFor(draft.start_when);
  // "Otra fecha" no cabe en el borrador —ahí sólo vive el string que viaja—, así
  // que se deduce al montar: hay algo escrito y no es ninguna de las píldoras.
  const [otherDate, setOtherDate] = useState(() => !!draft.start_when.trim() && preset === null);

  const togglePhysical = (chip: string) =>
    onDraftChange((prev) => ({
      ...prev,
      physical_chips: prev.physical_chips.includes(chip)
        ? prev.physical_chips.filter((item) => item !== chip)
        : [...prev.physical_chips, chip],
    }));

  return (
    <>
      <Field
        label={t.availability.relocate}
        error={errors['availability.relocate']}
      >
        <div className="flex flex-wrap gap-2 mt-1">
          <Toggle
            field="availability.relocate"
            selected={draft.relocate === true}
            onClick={() => set('relocate', true)}
          >
            {t.availability.relocateYes}
          </Toggle>
          <Toggle selected={draft.relocate === false} onClick={() => set('relocate', false)}>
            {t.availability.relocateNo}
          </Toggle>
        </div>
      </Field>

      {/* Era un campo de texto libre, y lo que producía eran cuarenta maneras de
          escribir "ya". Cuatro píldoras dicen lo mismo sin abrir el teclado; la
          quinta guarda el caso que no cabe. El valor que viaja sigue siendo el
          mismo string libre del contrato. */}
      <Field label={t.availability.startWhen} error={errors['availability.start_when']}>
        <div className="flex flex-wrap gap-2 mt-1">
          {START_WHEN_OPTIONS.map((option, i) => (
            <Toggle
              key={option.id}
              field={i === 0 ? 'availability.start_when' : undefined}
              selected={!otherDate && preset === option.id}
              onClick={() => {
                setOtherDate(false);
                set('start_when', option.value);
              }}
            >
              {t.options.startWhen[option.id]}
            </Toggle>
          ))}
          <Toggle
            selected={otherDate}
            onClick={() => {
              setOtherDate(true);
              if (preset !== null) set('start_when', '');
            }}
          >
            {t.availability.otherDate}
          </Toggle>
        </div>

        {otherDate && (
          <input
            className={`input w-full mt-3${errors['availability.start_when'] ? ' input-error' : ''}`}
            aria-label={t.availability.otherDateLabel}
            placeholder={t.availability.otherDatePlaceholder}
            value={preset === null ? draft.start_when : ''}
            maxLength={120}
            onChange={(e) => set('start_when', e.target.value)}
          />
        )}
      </Field>

      <Field label={t.availability.physical} optional error={errors.physical_capabilities}>
        <p className="text-xs hush mb-2">{t.availability.physicalIntro}</p>

        <div className="flex flex-wrap gap-2">
          {PHYSICAL_CHIPS.map((chip) => (
            <Toggle
              key={chip}
              selected={draft.physical_chips.includes(chip)}
              onClick={() => togglePhysical(chip)}
            >
              {t.options.physicalChips[PHYSICAL_CHIP_IDS[chip]]}
            </Toggle>
          ))}
        </div>

        <div className="mt-3">
          <VoiceAnswer
            id="physical_capabilities"
            field={PHYSICAL_FIELD}
            question={t.coachQuestions.physical}
            invitation={t.availability.physicalInvitation}
            placeholder={t.availability.physicalPlaceholder}
            value={draft.physical_capabilities}
            maxLength={physicalRoomLeft(draft.physical_chips)}
            rows={3}
            invalid={!!errors.physical_capabilities}
            onChange={(value, mode) => {
              onDraftChange((prev) => ({
                ...prev,
                physical_capabilities: value,
                input_mode: noteInputMode(prev.input_mode, PHYSICAL_FIELD, mode, value),
              }));
              onClearError(PHYSICAL_FIELD);
            }}
            onQuality={(quality) => onQuality(PHYSICAL_FIELD, quality)}
          />
        </div>
      </Field>
    </>
  );
}
